import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, CheckCircle, XCircle, Send, ChevronDown, ChevronRight } from "lucide-react";

const PRIORITY_META = {
  low:      { label: "Low",      colour: "text-[#6B7280] bg-[#1F2937]" },
  medium:   { label: "Medium",   colour: "text-[#F59E0B] bg-[#F59E0B]/10" },
  high:     { label: "High",     colour: "text-[#FF007A] bg-[#FF007A]/10" },
  critical: { label: "Critical", colour: "text-[#FF007A] bg-[#FF007A]/20 font-bold" },
};

const STATUS_META = {
  draft:     { label: "Draft",     colour: "text-[#6B7280] bg-[#1F2937]" },
  submitted: { label: "Submitted", colour: "text-[#F59E0B] bg-[#F59E0B]/10" },
  approved:  { label: "Approved",  colour: "text-[#00F5D4] bg-[#00F5D4]/10" },
  rejected:  { label: "Rejected",  colour: "text-[#FF007A] bg-[#FF007A]/10" },
};

const Badge = ({ meta }) => (
  <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${meta.colour}`}>
    {meta.label}
  </span>
);

const CRCard = ({ cr, onUpdate, onDelete, isManager }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const transition = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/change-requests/${cr.cr_id}`, { status });
      onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#1F2937] bg-[#0D1117]" data-testid={`cr-card-${cr.cr_id}`}>
      <div
        className="flex items-start gap-4 px-5 py-4 cursor-pointer hover:bg-[#111827] transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-white text-sm">{cr.title}</span>
            <Badge meta={STATUS_META[cr.status] || STATUS_META.draft} />
            <Badge meta={PRIORITY_META[cr.priority] || PRIORITY_META.medium} />
          </div>
          <div className="text-[11px] text-[#6B7280] mt-1">
            Raised by {cr.requested_by || "unknown"} · {(cr.raised_at || "").slice(0, 10)}
            {cr.estimated_effort_days != null && ` · ${cr.estimated_effort_days}d effort`}
            {cr.estimated_cost_delta != null && ` · £${cr.estimated_cost_delta.toLocaleString()} cost delta`}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {cr.status === "draft" && (
            <Button
              size="sm"
              disabled={busy}
              onClick={(e) => { e.stopPropagation(); transition("submitted"); }}
              className="rounded-none h-7 bg-[#002FA7]/20 text-[#60A5FA] hover:bg-[#002FA7]/40 border border-[#002FA7]/30 text-xs"
            >
              <Send className="w-3 h-3 mr-1" /> Submit
            </Button>
          )}
          {isManager && cr.status === "submitted" && (
            <>
              <Button
                size="sm"
                disabled={busy}
                onClick={(e) => { e.stopPropagation(); transition("approved"); }}
                className="rounded-none h-7 bg-[#00F5D4]/10 text-[#00F5D4] hover:bg-[#00F5D4]/20 border border-[#00F5D4]/30 text-xs"
              >
                <CheckCircle className="w-3 h-3 mr-1" /> Approve
              </Button>
              <Button
                size="sm"
                disabled={busy}
                onClick={(e) => { e.stopPropagation(); transition("rejected"); }}
                className="rounded-none h-7 bg-[#FF007A]/10 text-[#FF007A] hover:bg-[#FF007A]/20 border border-[#FF007A]/30 text-xs"
              >
                <XCircle className="w-3 h-3 mr-1" /> Reject
              </Button>
            </>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(cr.cr_id); }}
            className="text-[#374151] hover:text-[#FF007A]"
            data-testid={`delete-cr-${cr.cr_id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          {expanded ? <ChevronDown className="w-4 h-4 text-[#6B7280]" /> : <ChevronRight className="w-4 h-4 text-[#6B7280]" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[#1F2937] px-5 py-4 space-y-3">
          {cr.description && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Description</div>
              <p className="text-sm text-[#9CA3AF] whitespace-pre-wrap">{cr.description}</p>
            </div>
          )}
          {cr.impact && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Impact</div>
              <p className="text-sm text-[#9CA3AF] whitespace-pre-wrap">{cr.impact}</p>
            </div>
          )}
          {cr.review_note && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Review note</div>
              <p className="text-sm text-[#9CA3AF]">{cr.review_note}</p>
              {cr.reviewed_by && (
                <p className="text-[11px] text-[#4B5563] mt-0.5">
                  — {cr.reviewed_by} · {(cr.reviewed_at || "").slice(0, 10)}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ChangeRequests = () => {
  const { projectId } = useParams();
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "owner";

  const [items, setItems] = useState([]);
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", impact: "",
    priority: "medium", estimated_effort_days: "", estimated_cost_delta: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [crs, proj] = await Promise.all([
        api.get(`/projects/${projectId}/change-requests`),
        api.get(`/projects/${projectId}`).catch(() => ({ data: null })),
      ]);
      setItems(Array.isArray(crs.data) ? crs.data : (crs.data?.items ?? []));
      if (proj.data?.name) setProjectName(proj.data.name);
    } catch { toast.error("Failed to load change requests"); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/change-requests`, {
        title: form.title.trim(),
        description: form.description,
        impact: form.impact,
        priority: form.priority,
        estimated_effort_days: form.estimated_effort_days ? Number(form.estimated_effort_days) : null,
        estimated_cost_delta: form.estimated_cost_delta ? Number(form.estimated_cost_delta) : null,
      });
      toast.success("Change request created");
      setOpen(false);
      setForm({ title: "", description: "", impact: "", priority: "medium", estimated_effort_days: "", estimated_cost_delta: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const deleteCR = async (id) => {
    try {
      await api.delete(`/change-requests/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const counts = {
    total: items.length,
    pending: items.filter((c) => c.status === "submitted").length,
    approved: items.filter((c) => c.status === "approved").length,
    rejected: items.filter((c) => c.status === "rejected").length,
  };

  return (
    <div>
      <PageHeader
        backTo={`/workspace/${projectId}`}
        eyebrow={projectName || "Project"}
        title="Change Requests"
        description="Formal scope change control — log, submit, and approve changes to project scope or budget."
        actions={
          <div className="flex items-center gap-3">
            <Link to={`/workspace/${projectId}`} className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to project
            </Link>
            <Button onClick={() => setOpen(true)} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="new-cr-btn">
              <Plus className="w-4 h-4 mr-1" /> New request
            </Button>
          </div>
        }
      />

      {/* Stats strip */}
      <div className="grid grid-cols-4 gap-px bg-[#1F2937] border-b border-[#1F2937]">
        {[
          { label: "Total", val: counts.total, colour: "text-white" },
          { label: "Pending review", val: counts.pending, colour: "text-[#F59E0B]" },
          { label: "Approved", val: counts.approved, colour: "text-[#00F5D4]" },
          { label: "Rejected", val: counts.rejected, colour: "text-[#FF007A]" },
        ].map((s) => (
          <div key={s.label} className="bg-[#0D1117] px-5 sm:px-8 py-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">{s.label}</div>
            <div className={`text-3xl font-mono font-bold mt-1 ${s.colour}`}>{s.val}</div>
          </div>
        ))}
      </div>

      <div className="px-5 sm:px-8 lg:px-12 py-6 space-y-3">
        {loading ? (
          <div className="py-12 text-center text-sm text-[#6B7280]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-[#374151] py-16 text-center" data-testid="cr-empty">
            <div className="text-sm text-[#6B7280]">No change requests yet. Log the first scope change.</div>
          </div>
        ) : (
          items.map((cr) => (
            <CRCard key={cr.cr_id} cr={cr} onUpdate={load} onDelete={deleteCR} isManager={isManager} />
          ))
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="tracking-tight">New change request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Title *</Label>
              <Input
                className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                placeholder="e.g. Add reporting module to scope"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="cr-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Priority</Label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="w-full rounded-none mt-1 h-10 border border-[#374151] bg-[#030712] text-white px-3 text-sm"
                >
                  {Object.entries(PRIORITY_META).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Effort (days)</Label>
                <Input
                  type="number" min="0" step="0.5"
                  className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                  placeholder="Optional"
                  value={form.estimated_effort_days}
                  onChange={(e) => setForm({ ...form, estimated_effort_days: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Cost delta (£)</Label>
              <Input
                type="number" step="0.01"
                className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                placeholder="Optional — positive = increase, negative = decrease"
                value={form.estimated_cost_delta}
                onChange={(e) => setForm({ ...form, estimated_cost_delta: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Description</Label>
              <Textarea
                className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                rows={3}
                placeholder="What needs to change and why?"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="cr-desc-input"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Impact</Label>
              <Textarea
                className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                rows={2}
                placeholder="Effect on schedule, budget, or quality…"
                value={form.impact}
                onChange={(e) => setForm({ ...form, impact: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-none text-[#6B7280]">Cancel</Button>
            <Button onClick={create} disabled={busy} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="confirm-create-cr">
              {busy ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChangeRequests;
